import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  HelpCircle,
  Shield,
  TrendingUp,
} from "lucide-react";

export const HealthConditionSection: React.FC<{ data: any }> = ({ data }) => {
  const getConditionColor = (condition: string) => {
    switch (condition.toLowerCase()) {
      case "good":
        return "text-green-600 bg-green-50 border-green-200";
      case "fair":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "poor":
        return "text-red-600 bg-red-50 border-red-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  // Key Health Metrics using QuickStatsCard
  const healthMetrics = [
    {
      title: "Asset Condition Index",
      count: data?.strategicKpis?.assetHealthIndex?.value,
      displayValue: data?.strategicKpis?.assetHealthIndex?.value,
      trend: data?.strategicKpis?.assetHealthIndex?.trend,
      icon: <Shield className="h-8 w-8 text-green-500" />,
      tooltip:
        "A consolidated score from 0-100 indicating the overall health of the asset portfolio. It combines asset condition, age, and performance data.",
    },
    {
      title: "Total Assets",
      count: data?.strategicKpis?.totalAssets?.value,
      trend: data?.strategicKpis?.totalAssets?.trend,
      icon: <Activity className="h-8 w-8 text-blue-500" />,
      tooltip:
        "The total number of active assets currently being managed in the portfolio.",
    },
    {
      title: "Avg RUL",
      count: data?.strategicKpis?.avgRul?.value,
      trend: data?.strategicKpis?.avgRul?.trend,
      icon: <Clock className="h-8 w-8 text-purple-500" />,
      tooltip:
        "The average number of years remaining before assets in the portfolio are expected to reach the end of their service life.",
    },
    {
      title: "Asset Availability",
      count: `${data?.strategicKpis?.assetAvailability?.value ?? "NA"} ${
        data?.strategicKpis?.assetAvailability?.unit ?? "NA"
      }`,
      trend: data?.strategicKpis?.assetAvailability?.trend,
      icon: <CheckCircle className="h-8 w-8 text-green-500" />,
      tooltip:
        "The percentage of time assets were available to perform their required function during the selected period.",
    },
    {
      title: "Out of Service",
      count: data?.strategicKpis?.outOfService?.value,
      trend: data?.strategicKpis?.outOfService?.trend,
      icon: <AlertTriangle className="h-8 w-8 text-red-500" />,
      tooltip:
        "The total number of assets that are currently inactive or not operational.",
    },
    {
      title: "Inspection Compliance",
      count: `${data?.strategicKpis?.inspectionCompliance?.value ?? "NA"} ${
        data?.strategicKpis?.inspectionCompliance?.unit ?? "NA"
      }`,
      trend: data?.strategicKpis?.inspectionCompliance?.trend,
      icon: <TrendingUp className="h-8 w-8 text-blue-500" />,
      tooltip:
        "The percentage of required asset inspections that were completed by their due date within the selected period.",
    },
  ];

  const conditionDistribution = [
    {
      name: "Excellent",
      condition: "Excellent Condition",
      value: data?.assetHealthStatus?.excellentCondition?.count,
      percentage: data?.assetHealthStatus?.excellentCondition?.percentage,
      color: "bg-green-600",
    },
    {
      name: "Good",
      condition: "Good Condition",
      value: data?.assetHealthStatus?.goodCondition?.count,
      percentage: data?.assetHealthStatus?.goodCondition?.percentage,
      color: "bg-green-400",
    },
    {
      name: "Fair",
      condition: "Fair Condition",
      value: data?.assetHealthStatus?.fairCondition?.count,
      percentage: data?.assetHealthStatus?.fairCondition?.percentage,
      color: "bg-yellow-400",
    },
    {
      name: "Poor",
      condition: "Poor Condition",
      value: data?.assetHealthStatus?.poorCondition?.count,
      percentage: data?.assetHealthStatus?.poorCondition?.percentage,
      color: "bg-orange-500",
    },
    {
      name: "Critical",
      condition: "Critical Condition",
      value: data?.assetHealthStatus?.criticalCondition?.count,
      percentage: data?.assetHealthStatus?.criticalCondition?.percentage,
      color: "bg-red-600",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Key Health Metrics using QuickStatsCard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {healthMetrics.map((metric, index) => (
          <QuickStatsCard
            key={index}
            title={metric.title}
            count={metric.count ?? "NA"}
            displayValue={metric.displayValue}
            trend={metric.trend}
            icon={metric.icon}
            tooltip={metric.tooltip}
          />
        ))}
      </div>

      {/* Asset Categories and Age Profile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Asset Categories */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <div>
                <CardTitle className="page-title">Asset Classes</CardTitle>
              </div>
              <div className="mt-2">
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      A breakdown of the portfolio by asset Class.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <CardDescription className="page-subtitle">
              Distribution by asset class
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.assetCategories && data.assetCategories.length > 0 ? (
                data.assetCategories.map((category, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div className="flex items-center space-x-3">
                      <div>
                        <p className="font-medium">{category.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {category.percentage} % of total
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold">{category.count}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted-foreground py-6">
                  No asset categories available
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Asset Age Profile */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <div>
                <CardTitle className="page-title">Asset Age Profile</CardTitle>
              </div>
              <div className="mt-2">
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-gray-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      A breakdown of the portfolio by asset age. Click an age
                      band to analyze assets from that generation.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <CardDescription className="page-subtitle">
              Distribution by age bands
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.assetAgeProfile && data.assetAgeProfile.length > 0 ? (
                data.assetAgeProfile.map((age, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-4 h-4 rounded ${age.color}`}></div>
                      <div>
                        <p className="font-medium">{age.ageRange}</p>
                        <p className="text-sm text-muted-foreground">
                          {age.percentage}% of total
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold">{age.count}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-muted-foreground py-6">
                  No asset age profile data available
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Asset Health Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <div>
              <CardTitle className="page-title">
                Asset Condition Status
              </CardTitle>
            </div>
            <div className="mt-2">
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    The total number and percentage of assets currently assessed
                    as being in [Good/Fair/Poor] condition. Click to filter the
                    dashboard.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          <CardDescription className="page-subtitle">
            Current condition distribution
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {conditionDistribution?.map((condition, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg border-2 ${getConditionColor(
                  condition.name
                )}`}
              >
                <div className="text-center">
                  <p className="font-semibold text-lg">
                    {condition.name} Condition
                  </p>
                  <p className="text-2xl font-bold mt-2">{condition.value}</p>
                  <p className="text-sm opacity-75 mt-1">
                    {condition.percentage ?? "NA"} % of total
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
